import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MessageSquare, Phone, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

interface Solicitacao {
    id: string;
    assunto: string;
    mensagem: string;
    telefone_contato: string;
    status: string;
    created_at: string;
    alunos: { nome: string } | null;
}

export default function BotPortalTickets() {
    const { user } = useAuth();
    const escolaId = user?.escola_id || '';
    const [tickets, setTickets] = useState<Solicitacao[]>([]);
    const [loading, setLoading] = useState(true);
    const [pendingCloseId, setPendingCloseId] = useState<string | null>(null);

    const loadTickets = async () => {
        if (!escolaId) return;
        setLoading(true);
        try {
            const { data, error } = await (supabase as any)
                .from('solicitacoes_aluno')
                .select(`
                    id, assunto, mensagem, telefone_contato, status, created_at,
                    alunos ( nome )
                `)
                .eq('escola_id', escolaId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            
            // Filtro local: esconder solicitações concluídas há mais de 24h caso o cron ainda não tenha rodado
            const now = new Date();
            const filteredData = (data as Solicitacao[]).filter(ticket => {
                if (ticket.status.toLowerCase() === 'concluído' || ticket.status.toLowerCase() === 'fechado') {
                    const ticketDate = new Date(ticket.created_at);
                    const diffHours = (now.getTime() - ticketDate.getTime()) / (1000 * 60 * 60);
                    if (diffHours > 24) return false;
                }
                return true;
            });

            setTickets(filteredData);
        } catch (error: any) {
            console.error('Error fetching tickets:', error);
            toast({ title: 'Erro ao carregar solicitações', description: error.message, variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTickets();
    }, [escolaId]);

    const handleAtender = async (ticket: Solicitacao) => {
        const tel = ticket.telefone_contato?.replace(/\D/g, '') || '';
        if (!tel) {
            toast({ title: 'Erro', description: 'O aluno não informou um número de telefone válido.', variant: 'destructive' });
            return;
        }

        // Marcar como "Em andamento"
        if (ticket.status === 'aberto' || ticket.status === 'pendente') {
            await (supabase as any)
                .from('solicitacoes_aluno')
                .update({ status: 'em andamento' })
                .eq('id', ticket.id);
            setTickets(curr => curr.map(t => t.id === ticket.id ? { ...t, status: 'em andamento' } : t));
        }

        // Montar mensagem preenchida
        const msg = encodeURIComponent(`Olá ${ticket.alunos?.nome || 'Aluno(a)'}! Estou entrando em contato referente à sua solicitação no Portal do Aluno: *${ticket.assunto}*.\n\nComo posso ajudar?`);
        window.open(`https://wa.me/55${tel}?text=${msg}`, '_blank');
    };

    const handleConcluir = async (id: string) => {
        try {
            const { error } = await (supabase as any)
                .from('solicitacoes_aluno')
                .update({ status: 'concluído' })
                .eq('id', id);
            
            if (error) throw error;
            
            toast({ title: 'Ticket Fechado', description: 'Solicitação marcada como concluída.' });
            setTickets(curr => curr.map(t => t.id === id ? { ...t, status: 'concluído' } : t));
        } catch (err: any) {
            toast({ title: 'Erro', description: err.message, variant: 'destructive' });
        } finally {
            setPendingCloseId(null);
        }
    };

    const getStatusStyle = (status: string) => {
        const s = status.toLowerCase();
        if (s === 'aberto' || s === 'pendente') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        if (s === 'em andamento') return 'bg-blue-100 text-blue-800 border-blue-200';
        if (s === 'concluído' || s === 'fechado' || s === 'resolvido') return 'bg-green-100 text-green-800 border-green-200';
        return 'bg-gray-100 text-gray-800 border-gray-200';
    };

    const getStatusIcon = (status: string) => {
        const s = status.toLowerCase();
        if (s === 'aberto' || s === 'pendente') return <AlertCircle className="w-3.5 h-3.5" />;
        if (s === 'em andamento') return <Clock className="w-3.5 h-3.5" />;
        if (s === 'concluído' || s === 'fechado' || s === 'resolvido') return <CheckCircle2 className="w-3.5 h-3.5" />;
        return null;
    };

    return (
        <div className="flex min-h-[420px] flex-col overflow-hidden rounded-xl border bg-white xl:h-[calc(100dvh-220px)]">
            <div className="flex flex-col gap-4 border-b bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                <div>
                    <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-indigo-500" />
                        Solicitações do Portal Aluno
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">Gerencie os tickets de suporte abertos pelos alunos.</p>
                </div>
                <Button variant="outline" onClick={loadTickets} disabled={loading} className="w-full sm:w-auto">
                    Atualizar
                </Button>
            </div>

            <div className="flex-1 overflow-auto bg-slate-50/30 p-4 sm:p-5">
                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="flex h-full min-h-72 flex-col items-center justify-center px-4 text-center text-slate-600">
                        <MessageSquare className="mb-3 h-12 w-12 text-slate-300" />
                        <p className="text-lg font-medium text-slate-600">Nenhuma solicitação encontrada</p>
                        <p className="text-sm">Os tickets abertos pelos alunos aparecerão aqui.</p>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {tickets.map(ticket => {
                            const date = new Date(ticket.created_at);
                            const isClosed = ticket.status.toLowerCase() === 'concluído' || ticket.status.toLowerCase() === 'fechado';

                            return (
                                <div key={ticket.id} className={`relative overflow-hidden rounded-xl border bg-white p-4 transition-colors hover:border-indigo-200 sm:p-5 ${isClosed ? 'bg-slate-50' : ''}`}>
                                    {ticket.status.toLowerCase() === 'aberto' && (
                                        <div className="absolute top-0 right-0 w-2 h-full bg-yellow-400" />
                                    )}
                                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <Badge variant="outline" className={`capitalize flex items-center gap-1.5 px-2.5 py-0.5 border ${getStatusStyle(ticket.status)}`}>
                                                    {getStatusIcon(ticket.status)}
                                                    {ticket.status}
                                                </Badge>
                                                <span className="text-xs text-slate-500">
                                                    {format(date, "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}
                                                </span>
                                            </div>
                                            
                                            <h3 className="font-semibold text-slate-800 text-lg mb-1">{ticket.assunto}</h3>
                                            
                                            <div className="flex items-center gap-2 mb-3 text-sm text-slate-600">
                                                <span className="font-medium text-slate-700">{ticket.alunos?.nome || 'Aluno Desconhecido'}</span>
                                                <span className="text-slate-300">•</span>
                                                <div className="flex items-center gap-1.5 text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md">
                                                    <Phone className="w-3.5 h-3.5" />
                                                    {ticket.telefone_contato || 'Não informado'}
                                                </div>
                                            </div>

                                            <div className="bg-slate-50 border border-slate-100 p-3 rounded-lg text-sm text-slate-700 whitespace-pre-wrap">
                                                {ticket.mensagem}
                                            </div>
                                        </div>

                                        <div className="flex min-w-[160px] flex-col gap-2 sm:flex-row md:flex-col">
                                            <Button 
                                                variant="default" 
                                                className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white border-0 shadow-sm transition-all"
                                                onClick={() => handleAtender(ticket)}
                                            >
                                                <MessageSquare className="w-4 h-4 mr-2" />
                                                Atender WhatsApp
                                            </Button>

                                            {!isClosed && (
                                                <Button 
                                                    variant="outline" 
                                                    className="w-full border-slate-200 text-slate-600 hover:text-green-600 hover:border-green-200 hover:bg-green-50"
                                                    onClick={() => setPendingCloseId(ticket.id)}
                                                >
                                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                                    Marcar Concluído
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <ConfirmDialog
                open={pendingCloseId !== null}
                onOpenChange={(open) => !open && setPendingCloseId(null)}
                title="Concluir solicitação?"
                description="O ticket será marcado como concluído e sairá da lista ativa após o período de retenção."
                confirmLabel="Concluir ticket"
                onConfirm={() => pendingCloseId && handleConcluir(pendingCloseId)}
            />
        </div>
    );
}
